// AI Turnover Scheduler — Shared types (v1).
//
// Single source of truth for row-level and day-level risk flags.
// See `docs/business-plan/ai-turnover-scheduler-domain-glossary.md`.
//
// IMPORTANT: this module is a CLIENT v1. The same shapes must be honored by
// the future server-side engine (Epic 5) so that UI can switch source without
// changing types.

export type RiskLabel = "critico" | "attenzione" | "stabile";

export type RowSource = "platform" | "external";

/** Minimal shape needed to evaluate per-row risk flags. */
export interface SchedulerRowInput {
  source: RowSource;
  /** ISO datetime, start of the executable window (or scheduled time). */
  window_start_at: string | null;
  /** ISO datetime, end of the executable window. */
  window_end_at: string | null;
  /** "yyyy-mm-dd" — only meaningful for platform rows. */
  scheduled_date: string | null;
  /** "HH:mm" — only meaningful for platform rows. */
  scheduled_time: string | null;
  /** ISO datetime — guest checkout (start of turnover). */
  checkout_at: string | null;
  /** ISO datetime — next guest check-in (end of turnover). */
  checkin_at: string | null;
  /** Only for `external`: 'flexible' rows without start_at are unscheduled. */
  scheduling_precision?: "exact" | "window" | "date_only" | "flexible" | "platform";
  /** Only for `external`: nullable for `flexible`. */
  start_at?: string | null;
}

export interface RowRiskFlags {
  tight_window: boolean;
  urgent: boolean;
  unscheduled: boolean;
  same_day_turnover: boolean;
}

export interface RiskCounts {
  total: number;
  tight: number;
  urgent: number;
  unscheduled: number;
  same_day: number;
}

/** Day-level load + capacity facts for a single (company_id, day). */
export interface DayLoadInput {
  /** Total platform + external rows touching the day. */
  load: number;
  /** Declared sellable capacity for the day. */
  capacity: number;
  /** True if vendor closed the day or all slots blocked. */
  blocked: boolean;
  /** Number of rows on this day flagged as same_day_turnover. */
  sameDayTurnoverRows: number;
}

export interface DayLoadFlags {
  over_capacity_day: boolean;
  near_capacity_day: boolean;
  closed_day_with_load: boolean;
  same_day_turnover_cluster: boolean;
}

/** Returned by the engine for batch/portfolio aggregations. */
export interface SchedulerSummary {
  riskLabel: RiskLabel;
  counts: RiskCounts;
  /** Days that triggered any day-level flag, with their flag map. */
  dayFlags: Array<{ date: string } & DayLoadFlags>;
  suggestedAction: string;
  capacityContext: string | null;
}

/** Bumped whenever the rule semantics change (see glossary v.7). */
export const RULES_VERSION = "v1.0.0";

export const TIGHT_WINDOW_HOURS = 2;
export const URGENT_WINDOW_HOURS = 24;
/** Max days an external `window` / `date_only` is expanded across. Defensive. */
export const EXTERNAL_DAY_SPAN_CAP = 14;

// ---------------------------------------------------------------------------
// Server-side engine contract (Sprint 4 — wired in Sprint 5)
// ---------------------------------------------------------------------------
//
// Mirrors the JSONB shape returned by the supply/demand RPCs in
// `supabase/migrations/20260425113000_scheduler_engine_v1.sql` (see glossary
// section 8). Keep in sync: any change in SQL must bump RULES_VERSION above.

/** Closed enum of risk reason codes emitted by the server engine. */
export type RiskReasonCode =
  | "URGENT"
  | "TIGHT_WINDOW"
  | "UNSCHEDULED"
  | "SAME_DAY_TURNOVER"
  | "OVER_CAPACITY_DAY"
  | "NEAR_CAPACITY_DAY"
  | "CLOSED_DAY_WITH_LOAD"
  | "SAME_DAY_TURNOVER_CLUSTER";

export type RiskReasonScope = "rows" | "days";

export interface ServerRiskReason {
  code: RiskReasonCode;
  count: number;
  scope: RiskReasonScope;
}

export interface ServerDayFlag {
  date: string;
  over_capacity_day: boolean;
  near_capacity_day: boolean;
  closed_day_with_load: boolean;
  same_day_turnover_cluster: boolean;
  vendor_load: number;
  capacity_published: number;
}

/**
 * Standard payload returned by every `get_*_summary`, `get_*_status`,
 * `get_*_portfolio_*` RPC. `day_flags` is always present but is `[]` for
 * demand-side calls (privacy boundary).
 */
export interface ServerSchedulerSummary {
  risk_label: RiskLabel;
  risk_reasons: ServerRiskReason[];
  suggested_action: string;
  counts: RiskCounts;
  day_flags: ServerDayFlag[];
  /** Deterministic confidence in `[0, 1]`. See glossary 8.4. */
  confidence_score: number;
  rules_version: string;
  /** ISO timestamp from `now()` at decision time on the server. */
  computed_at: string;
}

/** Tabular row returned by `get_vendor_load_snapshot` / `get_vendor_capacity_snapshot`. */
export interface ServerVendorLoadDay {
  day: string;
  vendor_load: number;
  capacity_published: number;
  blocked: boolean;
  same_day_turnover_count: number;
  over_capacity_day: boolean;
  near_capacity_day: boolean;
  closed_day_with_load: boolean;
  same_day_turnover_cluster: boolean;
}

export interface ServerVendorCapacityDay {
  day: string;
  capacity_published: number;
  blocked: boolean;
  /** Raw per-slot breakdown returned by the RPC. */
  slots: Array<{ slot: string; capacity: number; blocked: boolean }>;
}

/** Tabular row returned by `get_batch_scheduler_rows`. */
export interface ServerBatchRowFlags {
  booking_id: string;
  effective_date: string | null;
  tight_window: boolean;
  urgent: boolean;
  unscheduled: boolean;
  same_day_turnover: boolean;
}

/**
 * Coarse confidence bucket for UI rendering. Pure derivation of
 * `confidence_score` — never surfaced numerically to non-technical users.
 */
export type ConfidenceLevel = "alta" | "media" | "bassa";
